import { getUserRecommendations } from '@atlaskit/smart-user-picker';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type StylesConfig,
} from '@databases/atlaskit/Select';
import { getCloudId } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { type KnownUser, type UserReference } from '@databases/types/confluence';
import { AvatarWithLabel, UserCard } from '@databases/user';
import React, { type ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getIdentity } from '@canvas/connection/identity';

const getOptionValue = (value: KnownUser | UserReference) => {
  return value.accountId;
};

const styles: StylesConfig<any> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
  singleValue: (base) => ({
    ...base,
    // @remark: Disabled as part of https://bitbucket.org/atlassian/canvas/pull-requests/27504/overview,
    // this should be re-enabled as soon as feasible
    // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
    lineHeight: '24px',
  }),
};

function isFullUser(value: KnownUser | UserReference): value is KnownUser {
  return 'type' in value;
}

interface UserPickerProps {
  value?: UserReference[];
  onChange: (value: UserReference[]) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isClearable?: boolean;
  isPending?: boolean;
  isDisabled?: boolean;
  isSeamless?: boolean;
  /** If true, render the selected value as a user mention rather than simple text. */
  isMentionPreview?: boolean;
}

const getUserRecommendationsParams = async (query: string) => {
  return {
    context: {
      contextType: 'contributors',
      principalId: (await getIdentity())?.accountId || '',
      productAttributes: {
        isEntitledConfluenceExternalCollaborator: false,
      },
      productKey: 'confluence',
      siteId: getCloudId() || '',
    },
    includeGroups: false,
    includeTeams: false,
    includeUsers: true,
    maxNumberOfResults: 15,
    performSearchQueryOnly: false,
    query,
  };
};

/**
 * An asynchronous select box that allows searching for users within the Confluence instance.
 */
const UserPicker: React.FunctionComponent<UserPickerProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isClearable,
  isPending,
  isDisabled = false,
  isSeamless,
}) => {
  const intl = useIntl();
  const formatMessage = intl.formatMessage;

  const loadOptions = useCallback(
    async (query: string): Promise<(KnownUser | UserReference)[]> => {
      const data = await getUserRecommendations(
        await getUserRecommendationsParams(query),
        intl
      );

      return data.map((user) => ({
        accountId: user.id,
        displayName: user.name,
        type: 'known',
        profilePicture: {
          path: user.avatarUrl,
        },
      }));
    },
    [intl]
  );

  const handleChange = useCallback(
    (
      val: ReadonlyArray<KnownUser | UserReference> | KnownUser | UserReference | null
    ) => {
      const values = Array.isArray(val) ? val : val ? [val] : [];
      onChange(values.map(({ accountId, displayName }) => ({ accountId, displayName })));
    },
    [onChange]
  );

  const formatOptionLabel = useCallback(
    (
      value: KnownUser | UserReference,
      meta: FormatOptionLabelMeta<KnownUser | UserReference>
    ): ReactNode => {
      if (meta.context === 'menu') {
        if (isFullUser(value)) {
          return <AvatarWithLabel userOrGroup={value} label={value.displayName} />;
        } else {
          return (
            <UserCard
              accountId={value.accountId}
              displayName={value.displayName}
              variant="avatar"
            />
          );
        }
      } else {
        return (
          <UserCard
            accountId={value.accountId}
            displayName={value.displayName}
            variant="mention"
          />
        );
      }
    },
    []
  );

  return (
    <div style={{ width: '100%' }}>
      <AsyncSelect<KnownUser | UserReference, boolean>
        inputId="database-user-picker"
        value={value}
        onChange={handleChange}
        isClearable={isClearable}
        formatOptionLabel={formatOptionLabel}
        loadOptions={loadOptions}
        getOptionValue={getOptionValue}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        placeholder={formatMessage(messages['select-dropdown.placeholder'])}
        loadingMessage={() =>
          formatMessage(messages['field-settings.select-picker-loading-text'])
        }
        isLoading={isPending}
        styles={styles}
        isDisabled={isDisabled}
        isSeamless={isSeamless}
        defaultMenuIsOpen={autoFocus && !value?.length}
        classNamePrefix="database-user-picker"
      />
    </div>
  );
};

export default UserPicker;
